import type {IssueFieldSingleSelectOption} from '@github-ui/issue-fields-shared/issue-field'
import {IssueSingleSelectFieldPicker} from '@github-ui/item-picker/IssueSingleSelectFieldPicker'
import {AlertIcon, GlobeIcon} from '@primer/octicons-react'
import {Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {type ComponentPropsWithoutRef, type Ref, useCallback, useId} from 'react'

import {getVisibilityIndicatorTooltip, LABELS} from './constants/labels'
import fieldItemStyles from './IssueFieldItem.module.css'
import {IssueFieldSingleSelectValueToken} from './IssueFieldSingleSelectValueToken'
import styles from './IssueFieldSingleSelectValueToken.module.css'
import {TooltipTriggerWrapper} from './utils/TooltipTriggerWrapper'

export type SingleSelectInitialValue = {
  name: string
  color: string
  description?: string
} | null
export type SingleSelectOnCommit = (fieldId: string, value: IssueFieldSingleSelectOption | null) => void

export type IssueFieldSingleSelectEditorProps = {
  fieldId: string
  fieldName: string
  readonly: boolean
  initialValue: SingleSelectInitialValue
  onCommit: SingleSelectOnCommit
  /// If set to true, then the single select editor should render in open state
  shouldOpen?: boolean
  isDraft?: boolean
  className?: string
  errorPresent?: boolean
  showVisibilityIndicator?: boolean
  isOwnerEnterpriseManaged?: boolean
}

type IssueFieldSingleSelectEditorButtonProps = {
  anchorProps: ComponentPropsWithoutRef<'button'>
  buttonRef: Ref<HTMLButtonElement> | undefined
  ariaLabel: string
  fieldName: string
  displayValue: React.ReactNode
  isNoValue: boolean
  className?: string
  errorPresent: boolean
  showVisibilityIndicator: boolean
  visibilityTooltipId?: string
}

const IssueFieldSingleSelectEditorButton = ({
  anchorProps,
  buttonRef,
  ariaLabel,
  fieldName,
  displayValue,
  isNoValue,
  className,
  errorPresent,
  showVisibilityIndicator,
  visibilityTooltipId,
}: IssueFieldSingleSelectEditorButtonProps) => {
  const content = (
    <button
      ref={buttonRef}
      className={clsx(fieldItemStyles.issueFieldFormWrapper, fieldItemStyles.issueFieldFormWrapperClickable, className)}
      aria-label={ariaLabel}
      {...anchorProps}
      aria-describedby={visibilityTooltipId}
    >
      <span className={fieldItemStyles.issueFieldLabel}>
        {fieldName}
        {showVisibilityIndicator && (
          <span className={fieldItemStyles.visibilityIconWrapper}>
            <GlobeIcon size={12} />
          </span>
        )}
      </span>
      <span
        className={clsx(
          fieldItemStyles.issueFieldValueText,
          !isNoValue && fieldItemStyles.hasValue,
          styles.compactValueContainer,
        )}
      >
        {displayValue}
      </span>
      {errorPresent && (
        <span className={fieldItemStyles.alertIconWrapper}>
          <AlertIcon className={fieldItemStyles.alertIcon} />
        </span>
      )}
    </button>
  )

  return errorPresent ? (
    <Tooltip text={LABELS.errorUpdatingIssueField} type="description">
      {content}
    </Tooltip>
  ) : (
    content
  )
}

export const IssueFieldSingleSelectEditor = ({
  fieldId,
  fieldName,
  readonly,
  initialValue,
  onCommit,
  shouldOpen = false,
  isDraft = false,
  className,
  errorPresent = false,
  showVisibilityIndicator = false,
  isOwnerEnterpriseManaged = false,
}: IssueFieldSingleSelectEditorProps) => {
  const visibilityTooltipId = useId()

  const onSelectionChanged = useCallback(
    (selectedOption: IssueFieldSingleSelectOption | null) => {
      onCommit(fieldId, selectedOption)
    },
    [fieldId, onCommit],
  )

  const onClose = useCallback(() => {
    if (isDraft) {
      onCommit(fieldId, null)
    }
  }, [fieldId, isDraft, onCommit])

  const isNoValue = !initialValue || (initialValue.name === '' && initialValue.color === '')

  const editableDisplayValue = isNoValue ? (
    LABELS.emptyValueLabels.singleSelect
  ) : (
    <IssueFieldSingleSelectValueToken
      name={initialValue.name}
      color={initialValue.color}
      getTooltipText={() => undefined}
      size="small"
      containerClassName={styles.compactContainer}
    />
  )
  const readonlyDisplayValue = isNoValue ? LABELS.setValue : editableDisplayValue

  const ariaLabel = isNoValue ? `Set ${fieldName}, none` : `Edit ${fieldName}, ${initialValue.name}`

  if (readonly) {
    return (
      <div className={clsx(fieldItemStyles.issueFieldFormWrapper, className)}>
        <p className={fieldItemStyles.issueFieldLabel}>{fieldName}</p>
        <div
          className={clsx(
            fieldItemStyles.issueFieldValueText,
            !isNoValue && fieldItemStyles.hasValue,
            styles.compactValueContainer,
          )}
        >
          {readonlyDisplayValue}
        </div>
      </div>
    )
  }

  return (
    <IssueSingleSelectFieldPicker
      fieldId={fieldId}
      fieldName={fieldName}
      selectedOption={initialValue ? initialValue.name : null}
      onSelectionChange={onSelectionChanged}
      anchorElement={(anchorProps, ref) => {
        const anchor = (
          <IssueFieldSingleSelectEditorButton
            anchorProps={anchorProps}
            buttonRef={ref}
            ariaLabel={ariaLabel}
            fieldName={fieldName}
            displayValue={editableDisplayValue}
            isNoValue={isNoValue}
            className={className}
            errorPresent={errorPresent}
            showVisibilityIndicator={showVisibilityIndicator}
            visibilityTooltipId={showVisibilityIndicator ? visibilityTooltipId : undefined}
          />
        )
        return showVisibilityIndicator ? (
          <Tooltip
            id={visibilityTooltipId}
            text={getVisibilityIndicatorTooltip(isOwnerEnterpriseManaged)}
            type="description"
            direction="e"
            delay="medium"
          >
            <TooltipTriggerWrapper>{anchor}</TooltipTriggerWrapper>
          </Tooltip>
        ) : (
          anchor
        )
      }}
      readonly={false}
      isLazy
      onClose={onClose}
      shouldOpen={shouldOpen}
    />
  )
}
